/**
 * Onboarding Wizard — guided, mobile-first multi-step flow that turns a
 * fresh account into a fully-seeded wedding plan in five short screens.
 *
 *   Welcome ➜ 1. Partner 1 ➜ 2. Partner 2 ➜ 3. Events ➜ 4. Budgets ➜ 5. Dates ➜ Review
 *
 * Progress is autosaved to localStorage after every keystroke so a refresh
 * doesn't lose answers. On a successful generate the cache is cleared. The
 * final CTA POSTs to `/api/onboarding/generate` which creates the wedding,
 * events (with `side` attribution), and a deduplicated checklist.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useWedding } from '../contexts/WeddingContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import DateInput from '../components/ui/date-input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { ArrowLeft, ArrowRight, Sparks, WarningTriangle, Heart, MagicWand, Xmark } from 'iconoir-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Steps — only steps with `step: n` appear in the "Step X of N" counter.
// The step list is computed at render time so single-side mode (`planning_scope === 'self'`)
// can collapse out the Partner 2 step entirely.
const ALL_STEP_KEYS = ['welcome', 'scope', 'partner1', 'partner2', 'events', 'budgets', 'dates', 'review'];
const TITLES = {
  welcome: 'Welcome',
  scope: 'Quick question',
  partner1: 'Partner 1',
  partner2: 'Partner 2',
  events: 'Events',
  budgets: 'Budgets',
  dates: 'Dates',
  review: 'Review',
};
function buildSteps(planningScope) {
  const keys = ALL_STEP_KEYS.filter((k) =>
    planningScope === 'self' ? k !== 'partner2' : true,
  );
  // Number every step except 'welcome' and 'review' so the progress bar
  // and "Step X of N" counter feel intuitive.
  let counter = 0;
  return keys.map((key) => {
    const numbered = key !== 'welcome' && key !== 'review';
    const step = numbered ? ++counter : null;
    return { key, title: TITLES[key], step };
  });
}

const CUSTOM_CULTURE = '__custom__';

// Maps a culture template id (e.g. "north_indian") to the human-readable
// label that the EVENT_CATALOG uses on each event's `cultures` list. Used
// by Step 3 to filter the event list down to the couple's traditions.
// Free-text custom cultures (CUSTOM_CULTURE) and unknown ids return null
// → "show everything" because we can't infer matching events.
const CULTURE_ID_TO_CATALOG_LABEL = {
  north_indian: 'North Indian',
  south_indian: 'South Indian',
  punjabi_sikh: 'Punjabi Sikh',
  pakistani: 'Pakistani',
  bengali: 'Bengali',
  gujarati_marwari: 'Gujarati / Marwari',
  christian: 'Christian',
  catholic: 'Catholic',
};

// Universal events that should appear regardless of culture selection and
// be pre-selected on Step 3. Defaults are intentionally neutral — we never
// assume Partner 1 is the bride or Partner 2 is the groom; the couple
// reassigns whichever side actually hosts each event with one tap. Users
// can also remove any event entirely. Seeded ONCE per onboarding session
// (guarded by `universals_seeded`) so navigating back/forward never
// clobbers their choices.
const UNIVERSAL_EVENT_DEFAULTS = {
  'Engagement': 'joint',
  'Bridal Shower': 'joint',
  'Bachelorette Party': 'joint',
  'Bachelor Party': 'joint',
};
const UNIVERSAL_EVENT_NAMES = new Set(Object.keys(UNIVERSAL_EVENT_DEFAULTS));

const EMPTY_FORM = {
  // Planning scope — set in new Step 1 ('scope'). 'self' means single-side
  // (Partner 2 step skipped entirely + every event auto-bucketed to partner1).
  // Empty string forces the user to pick before they can advance.
  planning_scope: '',
  partner1_first_name: '',
  partner1_last_name: '',
  partner1_culture: '',
  partner1_culture_custom: '',
  partner2_first_name: '',
  partner2_last_name: '',
  partner2_culture: '',
  partner2_culture_custom: '',
  // event_assignments: { [eventName]: 'partner1' | 'partner2' | 'joint' }
  event_assignments: {},
  // AI-suggested event names the user accepted that aren't in the catalog
  // (e.g. fusion ceremonies like "Mayun"). Tracked separately so they
  // survive a refresh and render in Step 3 alongside catalog events.
  custom_event_names: [],
  // Whether we've auto-seeded the universal events (Engagement, Bridal
  // Shower, etc.). Flipped to true after the first seed — prevents
  // re-seeding when the user removes a universal event.
  universals_seeded: false,
  partner1_budget: '',
  partner2_budget: '',
  joint_budget: '',
  // event_dates: { [eventName]: 'YYYY-MM-DD' | '' }
  event_dates: {},
  preferred_currency: 'USD',
};

const CURRENCIES = ['USD', 'INR', 'GBP', 'CAD', 'EUR', 'AUD', 'PKR', 'BDT', 'LKR', 'NPR'];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { fetchWeddings, selectWedding, templates, fetchTemplates } = useWedding();
  const { user } = useAuth();

  const storageKey = `shaadi_onboarding_v2_${user?.email || 'anon'}`;

  const [stepIdx, setStepIdx] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [hydrated, setHydrated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSkip, setShowSkip] = useState(false);
  const [catalog, setCatalog] = useState([]);

  // Steps recompute whenever planning_scope flips so the counter and
  // navigation stay in sync. We deliberately don't memoize on every form
  // field — only the scope affects step structure.
  const STEPS = useMemo(() => buildSteps(form.planning_scope), [form.planning_scope]);
  const TOTAL_NUMBERED = STEPS.filter((s) => s.step).length;
  const isSelfScope = form.planning_scope === 'self';

  // Load templates + event catalog once.
  useEffect(() => {
    if (!templates || templates.length === 0) fetchTemplates();
    let active = true;
    axios
      .get(`${API}/event-catalog`, { withCredentials: true })
      .then(({ data }) => { if (active) setCatalog(data.events || []); })
      .catch(() => { /* fall back to empty catalog */ });
    return () => { active = false; };
  }, [templates, fetchTemplates]);

  // Hydrate from localStorage on mount (once user email is known).
  useEffect(() => {
    if (!user?.email || hydrated) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && typeof saved === 'object') {
          setForm((prev) => ({ ...prev, ...saved }));
        }
      }
    } catch { /* ignore corrupt cache */ }
    setHydrated(true);
  }, [user?.email, storageKey, hydrated]);

  // Autosave form to localStorage.
  useEffect(() => {
    if (!hydrated || !user?.email) return;
    try { localStorage.setItem(storageKey, JSON.stringify(form)); } catch { /* quota */ }
  }, [form, hydrated, user?.email, storageKey]);

  // Seed universal events (Engagement, Bridal Shower, Bachelorette/Bachelor
  // Party) with sensible default buckets once the catalog + hydrated state
  // are both ready. Guarded by `universals_seeded` so this only runs on the
  // very first visit — once the user makes a choice (including removing a
  // seeded event), we never override them.
  useEffect(() => {
    if (!hydrated || catalog.length === 0) return;
    if (form.universals_seeded) return;
    // In single-side mode every universal event lands in partner1's bucket
    // (they're hosting their own side); in shared mode keep the existing
    // 'joint' default so neither family is presumed to host.
    const bucketFor = (name) => (isSelfScope ? 'partner1' : UNIVERSAL_EVENT_DEFAULTS[name]);
    const catalogNames = new Set(catalog.map((e) => e.name));
    const next = { ...form.event_assignments };
    let added = false;
    for (const name of Object.keys(UNIVERSAL_EVENT_DEFAULTS)) {
      if (!catalogNames.has(name)) continue;
      if (!next[name]) {
        next[name] = bucketFor(name);
        added = true;
      }
    }
    setForm((f) => ({
      ...f,
      event_assignments: added ? next : f.event_assignments,
      universals_seeded: true,
    }));
  }, [hydrated, catalog, form.universals_seeded, form.event_assignments, isSelfScope]);

  const update = useCallback((patch) => setForm((f) => ({ ...f, ...patch })), []);
  const currentStep = STEPS[stepIdx];
  const isLast = stepIdx === STEPS.length - 1;
  const isFirst = stepIdx === 0;

  // Resolved culture (either template id or the free-text value).
  const cultureLabel = (form, which) => {
    const picked = form[`${which}_culture`];
    if (!picked) return '';
    if (picked === CUSTOM_CULTURE) return form[`${which}_culture_custom`] || 'Custom';
    const tpl = (templates || []).find((t) => t.id === picked);
    return tpl ? tpl.name.replace(/ Wedding$/, '') : picked;
  };

  const partnerDisplayName = (which) => {
    const first = form[`${which}_first_name`]?.trim();
    return first || (which === 'partner1' ? 'Partner 1' : 'Partner 2');
  };

  // Resolve each partner's culture to the catalog label set (skip free-text
  // customs since we have nothing to filter on). In single-side mode we
  // ignore partner2's culture entirely.
  const selectedCultureLabels = useMemo(() => {
    const labels = new Set();
    const cultures = isSelfScope
      ? [form.partner1_culture]
      : [form.partner1_culture, form.partner2_culture];
    for (const id of cultures) {
      const label = CULTURE_ID_TO_CATALOG_LABEL[id];
      if (label) labels.add(label);
    }
    return labels;
  }, [form.partner1_culture, form.partner2_culture, isSelfScope]);

  const hasAnyCultureFilter = selectedCultureLabels.size > 0;

  // Events list shown in Step 3 — split into two groups:
  //   primaryEvents  — events from the couple's selected cultures + the four
  //                    universal events + any AI-suggested customs they
  //                    accepted + any event already assigned (so reassigning
  //                    via the AI suggestion is preserved).
  //   otherEvents    — everything else. Hidden by default; a toggle in
  //                    EventsStep reveals them when the couple wants to add
  //                    a ceremony from another tradition.
  // When the couple picks free-text/custom for both cultures, we show
  // everything in `primaryEvents` (no filter possible).
  const { primaryEvents, otherEvents } = useMemo(() => {
    const catalogNames = new Set(catalog.map((e) => e.name));
    const extras = (form.custom_event_names || [])
      .filter((n) => !catalogNames.has(n))
      .map((n) => ({
        name: n,
        cultures: ['AI suggestion'],
        description: '',
        default_budget_pct: 0,
        checklist_count: 0,
      }));
    const all = [...catalog, ...extras].sort((a, b) => a.name.localeCompare(b.name));

    if (!hasAnyCultureFilter) {
      // No filter — show everything in the primary list.
      return { primaryEvents: all, otherEvents: [] };
    }

    const assignedNames = new Set(Object.keys(form.event_assignments || {}));
    const primary = [];
    const other = [];
    for (const ev of all) {
      const evCultures = ev.cultures || [];
      const matchesSelected = evCultures.some((c) => selectedCultureLabels.has(c));
      const isUniversal = UNIVERSAL_EVENT_NAMES.has(ev.name);
      const isCustom = ev.cultures?.includes('AI suggestion');
      const isAssigned = assignedNames.has(ev.name);
      if (matchesSelected || isUniversal || isCustom || isAssigned) {
        primary.push(ev);
      } else {
        other.push(ev);
      }
    }
    return { primaryEvents: primary, otherEvents: other };
  }, [catalog, form.custom_event_names, form.event_assignments, hasAnyCultureFilter, selectedCultureLabels]);

  // Events actually selected (union of all three buckets).
  const selectedEvents = useMemo(
    () => Object.keys(form.event_assignments).filter((n) => form.event_assignments[n]),
    [form.event_assignments],
  );

  const canProceed = (() => {
    if (currentStep.key === 'scope') {
      return form.planning_scope === 'self' || form.planning_scope === 'both';
    }
    if (currentStep.key === 'partner1') {
      return Boolean(form.partner1_first_name.trim()) && Boolean(form.partner1_culture);
    }
    if (currentStep.key === 'partner2') {
      return Boolean(form.partner2_first_name.trim()) && Boolean(form.partner2_culture);
    }
    if (currentStep.key === 'events') {
      return selectedEvents.length > 0;
    }
    return true;
  })();

  const handleNext = () => {
    if (!canProceed) return;
    setStepIdx(Math.min(stepIdx + 1, STEPS.length - 1));
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handleBack = () => {
    setStepIdx(Math.max(stepIdx - 1, 0));
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resolveCultureForPayload = (which) => {
    const picked = form[`${which}_culture`];
    if (!picked) return null;
    if (picked === CUSTOM_CULTURE) return form[`${which}_culture_custom`]?.trim() || null;
    return picked;
  };

  const handleSkip = () => {
    setShowSkip(false);
    navigate('/dashboard');
  };

  const handleGenerate = async () => {
    setSubmitting(true);
    try {
      // Partition selected events into the three buckets based on assignments.
      const p1 = [];
      const p2 = [];
      const joint = [];
      for (const name of selectedEvents) {
        const bucket = form.event_assignments[name];
        if (bucket === 'partner1') p1.push(name);
        else if (bucket === 'partner2') p2.push(name);
        else if (bucket === 'joint') joint.push(name);
      }
      const payload = {
        planning_scope: form.planning_scope || 'both',
        partner1_first_name: form.partner1_first_name.trim() || null,
        partner1_last_name: form.partner1_last_name.trim() || null,
        partner1_culture: resolveCultureForPayload('partner1'),
        partner2_first_name: isSelfScope ? null : (form.partner2_first_name.trim() || null),
        partner2_last_name: isSelfScope ? null : (form.partner2_last_name.trim() || null),
        partner2_culture: isSelfScope ? null : resolveCultureForPayload('partner2'),
        preferred_currency: form.preferred_currency,
        partner1_event_names: p1,
        partner2_event_names: isSelfScope ? [] : p2,
        joint_event_names: isSelfScope ? [] : joint,
        partner1_family_budget: parseFloat(form.partner1_budget) || 0,
        partner2_family_budget: isSelfScope ? 0 : (parseFloat(form.partner2_budget) || 0),
        joint_budget: isSelfScope ? 0 : (parseFloat(form.joint_budget) || 0),
        event_dates: form.event_dates,
      };
      const { data } = await axios.post(`${API}/onboarding/generate`, payload, { withCredentials: true });
      toast.success(
        `Plan ready — ${data.events_created} events & ${data.checklist_items_created} tasks created`
      );
      try { localStorage.removeItem(storageKey); } catch { /* noop */ }
      await fetchWeddings();
      await selectWedding(data.wedding);
      navigate('/dashboard');
    } catch (err) {
      console.error(err);
      toast.error('Could not generate your plan. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen py-6 px-4 sm:py-10 relative overflow-hidden"
      data-testid="onboarding-page"
      style={{ background: '#FFFFFF' }}
    >
      {/* Soft decorative orbs */}
      <div className="absolute top-20 -left-20 w-72 h-72 rounded-full bg-terracotta/12 blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 -right-20 w-80 h-80 rounded-full bg-moss/10 blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-ochre/10 blur-3xl pointer-events-none" />
      <div className="max-w-xl mx-auto relative">
        <ProgressBar currentStep={currentStep} totalNumbered={TOTAL_NUMBERED} />
        <Card className="card-surface p-5 sm:p-8 mt-5 space-y-6">
          <header>
            {currentStep.step ? (
              <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] text-muted">
                Step {currentStep.step} of {TOTAL_NUMBERED}
              </p>
            ) : (
              <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] text-muted">
                {currentStep.key === 'welcome' ? 'Getting started' : 'All set'}
              </p>
            )}
            <h1 className="font-serif font-light text-3xl sm:text-4xl text-charcoal mt-1 tracking-tight" data-testid="onboarding-step-title">
              {stepTitle(currentStep, form)}
            </h1>
          </header>

          {currentStep.key === 'welcome' && (
            <WelcomeStep />
          )}
          {currentStep.key === 'scope' && (
            <ScopeStep form={form} update={update} />
          )}
          {currentStep.key === 'partner1' && (
            <PartnerStep which="partner1" form={form} update={update} templates={templates} isSelfScope={isSelfScope} />
          )}
          {currentStep.key === 'partner2' && (
            <PartnerStep which="partner2" form={form} update={update} templates={templates} isSelfScope={isSelfScope} />
          )}
          {currentStep.key === 'events' && (
            <EventsStep
              form={form}
              update={update}
              primaryEvents={primaryEvents}
              otherEvents={otherEvents}
              partner1Name={partnerDisplayName('partner1')}
              partner2Name={partnerDisplayName('partner2')}
              partner1Culture={form.partner1_culture}
              partner2Culture={form.partner2_culture}
              cultureLabelOf={cultureLabel}
              isSelfScope={isSelfScope}
            />
          )}
          {currentStep.key === 'budgets' && (
            <BudgetsStep
              form={form}
              update={update}
              partner1Name={partnerDisplayName('partner1')}
              partner2Name={partnerDisplayName('partner2')}
              isSelfScope={isSelfScope}
            />
          )}
          {currentStep.key === 'dates' && (
            <DatesStep
              form={form}
              update={update}
              selectedEvents={selectedEvents}
              isSelfScope={isSelfScope}
            />
          )}
          {currentStep.key === 'review' && (
            <ReviewStep
              form={form}
              selectedEvents={selectedEvents}
              partner1Culture={cultureLabel(form, 'partner1')}
              partner2Culture={cultureLabel(form, 'partner2')}
              partner1Name={partnerDisplayName('partner1')}
              partner2Name={partnerDisplayName('partner2')}
              isSelfScope={isSelfScope}
            />
          )}

          <FooterNav
            isFirst={isFirst}
            isLast={isLast}
            canProceed={canProceed}
            submitting={submitting}
            showSkipFirst={isFirst}
            onBack={handleBack}
            onNext={handleNext}
            onGenerate={handleGenerate}
            onSkip={() => setShowSkip(true)}
          />
        </Card>

        {hydrated && (form.partner1_first_name || form.partner2_first_name) && stepIdx > 0 && (
          <p
            className="text-center text-[10px] text-muted font-light mt-4 tracking-wider uppercase"
            data-testid="autosave-indicator"
          >
            Progress saved automatically
          </p>
        )}
      </div>

      <SkipDialog open={showSkip} onClose={() => setShowSkip(false)} onConfirm={handleSkip} />
    </div>
  );
}

function stepTitle(step, form) {
  if (step.key === 'scope') return 'Quick question';
  if (step.key === 'partner1') {
    return form.planning_scope === 'self' ? 'About you' : "Who's Partner 1?";
  }
  if (step.key === 'partner2') return "And Partner 2?";
  if (step.key === 'events') return 'Pick your events';
  if (step.key === 'budgets') return 'Budgets';
  if (step.key === 'dates') return 'Event dates';
  if (step.key === 'review') return 'One last look';
  return 'Welcome';
}

function ProgressBar({ currentStep, totalNumbered }) {
  const pct = currentStep.step
    ? (currentStep.step / totalNumbered) * 100
    : (currentStep.key === 'welcome' ? 0 : 100);
  return (
    <div className="space-y-2" data-testid="onboarding-progress">
      <div className="h-1 bg-border rounded-full overflow-hidden">
        <div
          className="h-full bg-terracotta transition-all duration-500 ease-out"
          style={{ width: `${pct}%` }}
          data-testid="onboarding-progress-bar"
        />
      </div>
    </div>
  );
}

function WelcomeStep() {
  return (
    <div className="space-y-4 text-charcoal" data-testid="welcome-step">
      <div className="flex items-center gap-2 text-terracotta">
        <Heart className="w-5 h-5" strokeWidth={1.5} />
        <span className="font-mono text-[10px] uppercase tracking-[0.2em]">Shaadi Planner</span>
      </div>
      <p className="text-base leading-relaxed">
        Welcome! We're going to ask you a few quick questions to set up your personalized wedding dashboard.
      </p>
      <p className="text-base leading-relaxed text-muted">
        Your wedding can include events hosted by each partner individually, as well as events you'll host together.
      </p>
      <p className="text-base leading-relaxed text-muted">
        Don't worry if you don't have everything finalized yet — you can update everything later.
      </p>
      <div className="bg-moss/8 border border-moss/25 rounded-sm p-4 flex items-start gap-3 mt-2">
        <Sparks className="w-4 h-4 text-moss flex-shrink-0 mt-0.5" />
        <p className="text-xs text-charcoal/70 leading-relaxed">
          This takes about 3 minutes. We'll save your answers as you go, so a refresh won't lose your progress.
        </p>
      </div>
    </div>
  );
}

// Scope picker — first real question of the wizard. Decides whether the
// rest of the flow includes Partner 2 + joint-event accounting.
function ScopeStep({ form, update }) {
  const options = [
    {
      value: 'both',
      title: "Both families' events",
      desc: "We're planning events for both partners and any joint celebrations together.",
    },
    {
      value: 'self',
      title: 'Just my events',
      desc: "I'm only planning the events on my side. Skip the second partner step and keep everything focused.",
    },
  ];
  return (
    <div className="space-y-4" data-testid="scope-step">
      <p className="text-sm text-muted leading-relaxed">
        Are you planning events for both families, or just your own side?
      </p>
      <div className="grid gap-3">
        {options.map((opt) => {
          const selected = form.planning_scope === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => update({ planning_scope: opt.value })}
              data-testid={`scope-option-${opt.value}`}
              className={`text-left p-4 rounded-sm border-2 transition-all ${
                selected
                  ? 'border-terracotta bg-terracotta/5 shadow-sm'
                  : 'border-border bg-white hover:border-charcoal/50'
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`flex-shrink-0 w-4 h-4 rounded-full border-2 mt-0.5 ${
                    selected ? 'border-terracotta bg-terracotta' : 'border-border bg-white'
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <p className={`font-serif text-base ${selected ? 'text-terracotta' : 'text-charcoal'}`}>
                    {opt.title}
                  </p>
                  <p className="text-xs text-muted mt-1 leading-relaxed">{opt.desc}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-muted leading-relaxed">
        You can switch between modes anytime from <span className="text-charcoal">Settings → Wedding</span>.
      </p>
    </div>
  );
}

function PartnerStep({ which, form, update, templates, isSelfScope }) {
  const idx = which === 'partner1' ? 1 : 2;
  const ident = which; // 'partner1' | 'partner2'
  const cultureVal = form[`${ident}_culture`];
  const tplOptions = (templates || []).map((t) => ({
    id: t.id,
    label: t.name.replace(/ Wedding$/, ''),
  }));
  // Self-mode reframes the only partner step as "About you" instead of
  // "Tell us about Partner 1" so the language matches the user's intent.
  const intro = isSelfScope && which === 'partner1'
    ? "Tell us about yourself. We'll personalize the plan around your culture and traditions."
    : `Tell us about Partner ${idx}. This helps us personalize the plan — you can tweak anything later.`;
  return (
    <div className="space-y-5" data-testid={`${ident}-step`}>
      <p className="text-sm text-muted leading-relaxed">{intro}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-[0.15em] text-muted">First name</Label>
          <Input
            value={form[`${ident}_first_name`]}
            onChange={(e) => update({ [`${ident}_first_name`]: e.target.value })}
            className="input-field h-11"
            placeholder={idx === 1 ? 'e.g., Riya' : 'e.g., Arjun'}
            data-testid={`${ident}-first-name-input`}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-[0.15em] text-muted">Last name</Label>
          <Input
            value={form[`${ident}_last_name`]}
            onChange={(e) => update({ [`${ident}_last_name`]: e.target.value })}
            className="input-field h-11"
            placeholder="Optional"
            data-testid={`${ident}-last-name-input`}
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-[11px] uppercase tracking-[0.15em] text-muted">Culture / Religion</Label>
        <Select
          value={cultureVal || undefined}
          onValueChange={(v) => update({ [`${ident}_culture`]: v })}
        >
          <SelectTrigger className="input-field h-11" data-testid={`${ident}-culture-select`}>
            <SelectValue placeholder="Choose one" />
          </SelectTrigger>
          <SelectContent>
            {tplOptions.map((t) => (
              <SelectItem key={t.id} value={t.id} data-testid={`${ident}-culture-opt-${t.id}`}>
                {t.label}
              </SelectItem>
            ))}
            <SelectItem value={CUSTOM_CULTURE} data-testid={`${ident}-culture-opt-custom`}>
              Other / Custom…
            </SelectItem>
          </SelectContent>
        </Select>
        {cultureVal === CUSTOM_CULTURE && (
          <Input
            value={form[`${ident}_culture_custom`]}
            onChange={(e) => update({ [`${ident}_culture_custom`]: e.target.value })}
            className="input-field h-11 mt-2"
            placeholder="Describe the tradition (e.g., Jain, Tamil Brahmin, Secular…)"
            data-testid={`${ident}-culture-custom-input`}
          />
        )}
        <p className="text-[11px] text-muted font-light leading-relaxed mt-1">
          We'll use this to suggest events and pre-built task lists — nothing is locked in.
        </p>
      </div>
    </div>
  );
}

function EventsStep({ form, update, primaryEvents, otherEvents, partner1Name, partner2Name, partner1Culture, partner2Culture, isSelfScope }) {
  // In single-side mode every event collapses into the user's one bucket
  // (`partner1`); we still write to `event_assignments` so the shared
  // generation pipeline keeps working unchanged. In shared mode users pick
  // between Partner 1 / Partner 2 / Joint as before.
  const BUCKETS = isSelfScope
    ? [{ key: 'partner1', label: 'Selected', short: 'Add' }]
    : [
        { key: 'partner1', label: `${partner1Name}'s events`, short: partner1Name },
        { key: 'partner2', label: `${partner2Name}'s events`, short: partner2Name },
        { key: 'joint',    label: 'Joint events',              short: 'Joint' },
      ];
  const assignments = form.event_assignments || {};

  const setAssignment = (name, bucket) => {
    const next = { ...(form.event_assignments || {}) };
    if (assignments[name] === bucket) {
      delete next[name]; // toggle off
    } else {
      next[name] = bucket;
    }
    update({ event_assignments: next });
  };

  // Self-mode binary toggle (Add / Remove) — uses the single 'partner1' bucket.
  const toggleSelfEvent = (name) => {
    const next = { ...(form.event_assignments || {}) };
    if (next[name]) {
      delete next[name];
    } else {
      next[name] = 'partner1';
    }
    update({ event_assignments: next });
  };

  const countFor = (bucket) =>
    Object.values(assignments).filter((b) => b === bucket).length;

  // ── AI suggestion state ─────────────────────────────────────────────
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestError, setSuggestError] = useState('');
  const [dismissedNames, setDismissedNames] = useState([]);
  // Filter out suggestions the user has dismissed or already accepted.
  const visibleSuggestions = suggestions.filter(
    (s) => !dismissedNames.includes(s.name) && !assignments[s.name],
  );
  const canSuggest = Boolean(partner1Culture || partner2Culture);

  const fetchSuggestions = async () => {
    setSuggestError('');
    setSuggestLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/onboarding/suggest-events`,
        {
          partner1_culture: partner1Culture || null,
          partner2_culture: partner2Culture || null,
          selected_event_names: Object.keys(assignments),
          partner1_first_name: partner1Name,
          partner2_first_name: partner2Name,
        },
        { withCredentials: true },
      );
      const incoming = (data?.suggestions || []).filter(
        (s) => !assignments[s.name] && !suggestions.some((x) => x.name === s.name),
      );
      if (incoming.length === 0 && (data?.suggestions || []).length === 0) {
        setSuggestError('No new ideas right now — try again after picking more events.');
      } else {
        setSuggestions((prev) => [...prev, ...incoming]);
      }
    } catch (err) {
      console.error(err);
      setSuggestError('Could not get suggestions. Please try again.');
    } finally {
      setSuggestLoading(false);
    }
  };

  const acceptSuggestion = (suggestion, bucket) => {
    // In single-side mode collapse every accepted suggestion into the user's
    // own bucket regardless of what the AI proposed.
    const targetBucket = isSelfScope ? 'partner1' : (bucket || suggestion.bucket || 'joint');
    // If the event is not in the catalog, add it to custom_event_names so
    // it renders in the Step 3 list and persists through generate.
    if (!suggestion.in_catalog) {
      const nextCustom = Array.from(new Set([...(form.custom_event_names || []), suggestion.name]));
      update({
        custom_event_names: nextCustom,
        event_assignments: { ...assignments, [suggestion.name]: targetBucket },
      });
    } else {
      update({
        event_assignments: { ...assignments, [suggestion.name]: targetBucket },
      });
    }
    setDismissedNames((prev) => [...prev, suggestion.name]);
  };

  const dismissSuggestion = (name) => {
    setDismissedNames((prev) => [...prev, name]);
  };

  // Whether the user has expanded the "events from other traditions" group.
  const [showOther, setShowOther] = useState(false);

  if (primaryEvents.length === 0 && otherEvents.length === 0) {
    return (
      <div className="space-y-3 text-sm text-muted" data-testid="events-step-empty">
        Loading events…
      </div>
    );
  }

  // Helper: render one event row + its bucket buttons. Used for both lists.
  const renderRow = (ev) => {
    const current = assignments[ev.name];
    const safeKey = ev.name.replace(/[^a-zA-Z0-9]/g, '-');
    return (
      <div key={ev.name} className="px-3 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-charcoal font-medium truncate">{ev.name}</p>
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted truncate">
            {(ev.cultures || []).slice(0, 3).join(' · ') || 'Shared'}
          </p>
        </div>
        {isSelfScope ? (
          <div className="flex flex-shrink-0" data-testid={`event-row-${safeKey}`}>
            <button
              type="button"
              onClick={() => toggleSelfEvent(ev.name)}
              data-testid={`toggle-self-event-${safeKey}`}
              className={`px-3.5 py-1.5 text-[11px] rounded-full border transition-colors ${
                current
                  ? 'bg-charcoal text-white border-charcoal'
                  : 'bg-white text-muted border-border hover:border-charcoal hover:text-charcoal'
              }`}
            >
              {current ? '✓ Added' : '+ Add'}
            </button>
          </div>
        ) : (
          <div
            className="flex gap-1.5 sm:gap-1 flex-shrink-0"
            data-testid={`event-row-${safeKey}`}
          >
            {BUCKETS.map((b) => {
              const active = current === b.key;
              return (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => setAssignment(ev.name, b.key)}
                  data-testid={`assign-${b.key}-${safeKey}`}
                  className={`flex-1 sm:flex-none px-2.5 py-1.5 text-[11px] rounded-full border transition-colors ${
                    active
                      ? 'bg-charcoal text-white border-charcoal'
                      : 'bg-white text-muted border-border hover:border-charcoal hover:text-charcoal'
                  }`}
                >
                  {b.short}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="events-step">
      <p className="text-sm text-muted leading-relaxed">
        {isSelfScope
          ? 'Pick the events you want to plan. Tap to add or remove. You can add or rename events later.'
          : `For each event, tap who's hosting it: ${partner1Name}, ${partner2Name}, or both of you together. Tap again to un-select.`}
      </p>

      {isSelfScope ? (
        <div className="flex flex-wrap gap-2 text-[11px] font-mono uppercase tracking-wider">
          <span
            data-testid="bucket-count-partner1"
            className="px-2.5 py-1 rounded-full bg-cream border border-border text-muted"
          >
            Selected: <span className="text-charcoal">{countFor('partner1')}</span>
          </span>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 text-[11px] font-mono uppercase tracking-wider">
          {BUCKETS.map((b) => (
            <span
              key={b.key}
              data-testid={`bucket-count-${b.key}`}
              className="px-2.5 py-1 rounded-full bg-cream border border-border text-muted"
            >
              {b.short}: <span className="text-charcoal">{countFor(b.key)}</span>
            </span>
          ))}
        </div>
      )}

      {/* AI Suggest more events */}
      <div data-testid="ai-suggest-section">
        {visibleSuggestions.length === 0 ? (
          <button
            type="button"
            onClick={fetchSuggestions}
            disabled={!canSuggest || suggestLoading}
            data-testid="ai-suggest-btn"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-terracotta/40 bg-terracotta/5 text-terracotta text-xs font-medium tracking-wide hover:bg-terracotta/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MagicWand className="w-3.5 h-3.5" strokeWidth={1.5} />
            {suggestLoading ? 'Thinking…' : (suggestions.length > 0 ? 'Suggest more events' : 'Suggest more events with AI')}
          </button>
        ) : (
          <div className="space-y-2" data-testid="ai-suggestion-cards">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-terracotta flex items-center gap-1.5">
                <MagicWand className="w-3.5 h-3.5" strokeWidth={1.5} />
                AI suggestions
              </p>
              <button
                type="button"
                onClick={fetchSuggestions}
                disabled={suggestLoading}
                className="text-[10px] font-mono uppercase tracking-wider text-muted hover:text-charcoal disabled:opacity-50"
                data-testid="ai-suggest-more-btn"
              >
                {suggestLoading ? 'Thinking…' : 'More ideas'}
              </button>
            </div>
            {visibleSuggestions.map((s) => {
              const key = s.name.replace(/[^a-zA-Z0-9]/g, '-');
              const bucketLabel = BUCKETS.find((b) => b.key === s.bucket)?.short || 'Joint';
              return (
                <div
                  key={s.name}
                  data-testid={`ai-suggestion-${key}`}
                  className="border border-terracotta/30 bg-terracotta/[0.03] rounded-sm p-3 space-y-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-charcoal font-medium">{s.name}</p>
                      {s.description && (
                        <p className="text-xs text-muted leading-snug mt-0.5">{s.description}</p>
                      )}
                      {s.why && (
                        <p className="text-[10px] font-mono uppercase tracking-wider text-terracotta/80 mt-1">
                          Why: {s.why}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => dismissSuggestion(s.name)}
                      data-testid={`ai-dismiss-${key}`}
                      className="text-muted hover:text-charcoal flex-shrink-0"
                      aria-label="Dismiss suggestion"
                    >
                      <Xmark className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-muted self-center mr-1">
                      Add to:
                    </span>
                    {BUCKETS.map((b) => {
                      const isDefault = b.key === s.bucket;
                      return (
                        <button
                          key={b.key}
                          type="button"
                          onClick={() => acceptSuggestion(s, b.key)}
                          data-testid={`ai-accept-${b.key}-${key}`}
                          className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${
                            isDefault
                              ? 'bg-charcoal text-white border-charcoal hover:opacity-90'
                              : 'bg-white text-charcoal border-border hover:border-charcoal'
                          }`}
                        >
                          {b.short}
                          {isDefault && <span className="ml-1 text-[9px] opacity-70">·suggested</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {suggestError && (
          <p className="text-[11px] text-terracotta mt-2" data-testid="ai-suggest-error">{suggestError}</p>
        )}
        {!canSuggest && (
          <p className="text-[11px] text-muted mt-1">
            Pick a culture for at least one partner to unlock AI suggestions.
          </p>
        )}
      </div>

      <div className="border border-border rounded-sm divide-y divide-border bg-white max-h-[420px] overflow-y-auto" data-testid="event-assignment-list">
        {primaryEvents.map(renderRow)}
      </div>

      {otherEvents.length > 0 && (
        <div data-testid="other-traditions-section">
          {!showOther ? (
            <button
              type="button"
              onClick={() => setShowOther(true)}
              data-testid="show-other-traditions-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-border bg-white text-charcoal/70 text-xs font-medium tracking-wide hover:border-charcoal hover:text-charcoal transition-colors"
            >
              + Show events from other traditions ({otherEvents.length})
            </button>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-muted">
                  Other traditions
                </p>
                <button
                  type="button"
                  onClick={() => setShowOther(false)}
                  data-testid="hide-other-traditions-btn"
                  className="text-[10px] font-mono uppercase tracking-wider text-muted hover:text-charcoal"
                >
                  Hide
                </button>
              </div>
              <p className="text-[11px] text-muted leading-relaxed">
                Mix and match — add anything that fits your celebration.
              </p>
              <div
                className="border border-border rounded-sm divide-y divide-border bg-white max-h-[320px] overflow-y-auto"
                data-testid="other-events-list"
              >
                {otherEvents.map(renderRow)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BudgetsStep({ form, update, partner1Name, partner2Name, isSelfScope }) {
  const currency = form.preferred_currency || 'USD';
  const totals = isSelfScope
    ? (parseFloat(form.partner1_budget) || 0)
    : (parseFloat(form.partner1_budget) || 0)
      + (parseFloat(form.partner2_budget) || 0)
      + (parseFloat(form.joint_budget) || 0);
  return (
    <div className="space-y-5" data-testid="budgets-step">
      <p className="text-sm text-muted leading-relaxed">
        These can be rough estimates — you can refine them later.
      </p>

      <div className="space-y-1.5">
        <Label className="text-[11px] uppercase tracking-[0.15em] text-muted">Currency</Label>
        <Select value={form.preferred_currency} onValueChange={(v) => update({ preferred_currency: v })}>
          <SelectTrigger className="input-field h-11" data-testid="budgets-currency-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isSelfScope ? (
        <BudgetField
          label="Your budget"
          value={form.partner1_budget}
          onChange={(v) => update({ partner1_budget: v })}
          currency={currency}
          testid="onboarding-partner1-budget"
        />
      ) : (
        <>
          <BudgetField
            label={`${partner1Name}'s events`}
            value={form.partner1_budget}
            onChange={(v) => update({ partner1_budget: v })}
            currency={currency}
            testid="onboarding-partner1-budget"
          />
          <BudgetField
            label={`${partner2Name}'s events`}
            value={form.partner2_budget}
            onChange={(v) => update({ partner2_budget: v })}
            currency={currency}
            testid="onboarding-partner2-budget"
          />
          <BudgetField
            label="Joint events"
            value={form.joint_budget}
            onChange={(v) => update({ joint_budget: v })}
            currency={currency}
            testid="onboarding-joint-budget"
          />
        </>
      )}

      <div className="bg-cream border border-border rounded-sm p-3 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.15em] text-muted font-mono">Total</span>
        <span className="font-mono text-lg text-charcoal" data-testid="budgets-total">
          {currency} {totals.toLocaleString()}
        </span>
      </div>
    </div>
  );
}

function BudgetField({ label, value, onChange, currency, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-[0.15em] text-muted">{label}</Label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-mono text-muted">
          {currency}
        </span>
        <Input
          type="number"
          min="0"
          step="0.01"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="input-field h-11 font-mono pl-14"
          placeholder="0"
          data-testid={testid}
        />
      </div>
    </div>
  );
}

function DatesStep({ form, update, selectedEvents, isSelfScope }) {
  if (selectedEvents.length === 0) {
    return (
      <div className="space-y-3 text-sm text-muted" data-testid="dates-step-empty">
        <p>You haven't picked any events yet. Go back to the Events step to choose some.</p>
      </div>
    );
  }
  const setDate = (name, iso) => {
    update({ event_dates: { ...(form.event_dates || {}), [name]: iso || '' } });
  };
  // Bucket helper text — purely cosmetic, hidden in self-mode where there's
  // only one bucket anyway.
  const bucketLabel = (name) => {
    if (isSelfScope) return '';
    const b = form.event_assignments?.[name];
    if (b === 'joint') return 'Joint event';
    if (b === 'partner1') return "Partner 1's event";
    if (b === 'partner2') return "Partner 2's event";
    return '';
  };
  return (
    <div className="space-y-4" data-testid="dates-step">
      <p className="text-sm text-muted leading-relaxed">
        Add a date for any event you've already settled. Leave blank for anything that's still up in the air — you can fill it in later.
      </p>
      <div className="space-y-3">
        {selectedEvents.map((name) => {
          const key = name.replace(/[^a-zA-Z0-9]/g, '-');
          const helper = bucketLabel(name);
          return (
            <div key={name} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 pb-3 border-b border-border last:border-b-0">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-charcoal font-medium truncate">{name}</p>
                {helper && (
                  <p className="text-[10px] font-mono uppercase tracking-wider text-muted">{helper}</p>
                )}
              </div>
              <div className="flex items-center gap-2 sm:w-60">
                <DateInput
                  value={form.event_dates?.[name] || ''}
                  onChange={(iso) => setDate(name, iso)}
                  className="input-field h-10 font-mono flex-1"
                  data-testid={`date-input-${key}`}
                />
                {form.event_dates?.[name] ? (
                  <button
                    type="button"
                    onClick={() => setDate(name, '')}
                    className="text-[10px] font-mono uppercase tracking-wider text-muted hover:text-charcoal"
                    data-testid={`date-clear-${key}`}
                  >
                    Skip
                  </button>
                ) : (
                  <span className="text-[10px] font-mono uppercase tracking-wider text-muted">Optional</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReviewStep({ form, selectedEvents, partner1Culture, partner2Culture, partner1Name, partner2Name, isSelfScope }) {
  const currency = form.preferred_currency || 'USD';
  const total = isSelfScope
    ? (parseFloat(form.partner1_budget) || 0)
    : (parseFloat(form.partner1_budget) || 0)
      + (parseFloat(form.partner2_budget) || 0)
      + (parseFloat(form.joint_budget) || 0);
  const counts = { partner1: 0, partner2: 0, joint: 0 };
  for (const n of selectedEvents) {
    const b = form.event_assignments[n];
    if (b in counts) counts[b] += 1;
  }
  return (
    <div className="space-y-4" data-testid="onboarding-review">
      <p className="text-sm text-muted leading-relaxed">
        Looks good? Hit Generate and we'll build your dashboard. You can edit anything from here.
      </p>
      <dl className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
        <ReviewRow
          label="Planning scope"
          value={isSelfScope ? 'Just my events' : "Both families' events"}
        />
        {isSelfScope ? (
          <>
            <ReviewRow label="You" value={`${partner1Name}${partner1Culture ? ` · ${partner1Culture}` : ''}`} />
            <ReviewRow label="Selected events" value={`${selectedEvents.length} event${selectedEvents.length === 1 ? '' : 's'}`} />
            <ReviewRow label="Currency" value={currency} />
            <ReviewRow label="Your budget" value={`${currency} ${Number(form.partner1_budget || 0).toLocaleString()}`} />
          </>
        ) : (
          <>
            <ReviewRow label="Partner 1" value={`${partner1Name}${partner1Culture ? ` · ${partner1Culture}` : ''}`} />
            <ReviewRow label="Partner 2" value={`${partner2Name}${partner2Culture ? ` · ${partner2Culture}` : ''}`} />
            <ReviewRow label={`${partner1Name}'s events`} value={`${counts.partner1} event${counts.partner1 === 1 ? '' : 's'}`} />
            <ReviewRow label={`${partner2Name}'s events`} value={`${counts.partner2} event${counts.partner2 === 1 ? '' : 's'}`} />
            <ReviewRow label="Joint events" value={`${counts.joint} event${counts.joint === 1 ? '' : 's'}`} />
            <ReviewRow label="Currency" value={currency} />
            <ReviewRow label={`${partner1Name}'s budget`} value={`${currency} ${Number(form.partner1_budget || 0).toLocaleString()}`} />
            <ReviewRow label={`${partner2Name}'s budget`} value={`${currency} ${Number(form.partner2_budget || 0).toLocaleString()}`} />
            <ReviewRow label="Joint budget" value={`${currency} ${Number(form.joint_budget || 0).toLocaleString()}`} />
          </>
        )}
        <ReviewRow
          label="Total budget"
          value={`${currency} ${total.toLocaleString()}`}
          emphasised
        />
      </dl>
    </div>
  );
}

function ReviewRow({ label, value, emphasised }) {
  return (
    <>
      <dt className="text-[11px] font-mono uppercase tracking-[0.15em] text-muted">{label}</dt>
      <dd className={`text-charcoal ${emphasised ? 'font-serif text-base' : ''}`}>{value}</dd>
    </>
  );
}

function FooterNav({ isFirst, isLast, canProceed, submitting, showSkipFirst, onBack, onNext, onGenerate, onSkip }) {
  return (
    <div className="flex items-center justify-between pt-3 border-t border-border gap-3">
      <div>
        {showSkipFirst ? (
          <Button type="button" onClick={onSkip} className="btn-secondary text-xs" data-testid="onboarding-skip-btn">
            Skip for now
          </Button>
        ) : (
          <Button
            type="button"
            onClick={onBack}
            disabled={isFirst}
            className="btn-secondary text-xs flex items-center gap-1.5"
            data-testid="onboarding-back-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Button>
        )}
      </div>
      {isLast ? (
        <Button
          type="button"
          onClick={onGenerate}
          disabled={submitting}
          className="btn-primary text-xs flex items-center gap-1.5"
          data-testid="onboarding-generate-btn"
        >
          {submitting ? 'Generating…' : 'Generate plan'} <Sparks className="w-3.5 h-3.5" />
        </Button>
      ) : (
        <Button
          type="button"
          onClick={onNext}
          disabled={!canProceed}
          className="btn-primary text-xs flex items-center gap-1.5"
          data-testid="onboarding-next-btn"
        >
          Next <ArrowRight className="w-3.5 h-3.5" />
        </Button>
      )}
    </div>
  );
}

function SkipDialog({ open, onClose, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md" data-testid="onboarding-skip-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl flex items-center gap-2">
            <WarningTriangle className="w-5 h-5 text-ochre" /> Skip onboarding?
          </DialogTitle>
          <DialogDescription className="text-sm text-muted leading-relaxed pt-1">
            Skipping means we won't build a personalized plan for you. You'll start with an empty
            dashboard and add everything manually. You can restart the wizard anytime from your profile.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex gap-2 mt-4">
          <Button type="button" onClick={onClose} className="btn-secondary text-xs" data-testid="onboarding-skip-cancel-btn">
            Keep going
          </Button>
          <Button type="button" onClick={onConfirm} className="btn-primary text-xs" data-testid="onboarding-skip-confirm-btn">
            Skip anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
